import { ContextMenuOption } from '../../../models/context-menu-option';
import { Corpus, RepresentationSet, SharedState } from '../../../models/corpus';
import { DrRequestInfo } from '../../../models/dr-request-info';
import { Endpoint, EntityType } from '../../../models/enums';
import { NavigationInfo } from '../../../models/navigation-info';
import { ActionType, ObjectActionListType } from '../../../models/permission';
import { GenericTreeNode } from '../../generic-tree/generic-tree-node';

const importContextMenuOptions: Array<ContextMenuOption> = [
    new ContextMenuOption({
        name: 'Add Tags ...',
        iconClass: 'fa-tag', iconColor: 'color-grey-05', iconSecondClass: 'fa-plus', iconSecondColor: 'color-green-04',
        enabled: (item: GenericTreeNode) => (item?.data as Corpus).documentCount > 0,
        permissions: [{ permissionType: ObjectActionListType.DOCUMENT_TAGS, permissionActions: [ActionType.CREATE] },
        { permissionType: ObjectActionListType.ECA_CASE_DATA, permissionActions: [ActionType.CREATE] }]
    }),
    new ContextMenuOption({ divider: true }),
    new ContextMenuOption({
        name: 'Add to ...',
        iconClass: 'fa-folder', iconColor: 'color-yellow-05', iconSecondClass: 'fa-plus', iconSecondColor: 'color-green-04',
        enabled: (item: GenericTreeNode) => ((item?.data as Corpus).documentCount > 0) &&
            (item.data.representationType === RepresentationSet.VECTOR_SET ||
                item.data.representationType === RepresentationSet.CONTENT_INDEX),
        permissions: [{ permissionType: ObjectActionListType.ECA_CASE_DATA, permissionActions: [ActionType.CREATE], isMandatory: true },
        { permissionType: ObjectActionListType.DOCUMENT_FOLDER, permissionActions: [ActionType.CREATE] },
        { permissionType: ObjectActionListType.DOCUMENT_CUSTODIAN, permissionActions: [ActionType.CREATE] }],
        atLeastOne: true
    }),
    new ContextMenuOption({
        name: 'Add to Project Data',
        iconClass: 'fa-briefcase', iconColor: 'color-blue-grey-03', iconSecondClass: 'fa-plus', iconSecondColor: 'color-green-04',
        enabled: (item: GenericTreeNode) => (item?.data as Corpus).documentCount > 0 &&
            (item.data.representationType === RepresentationSet.VECTOR_SET || item.data.representationType === RepresentationSet.CONTENT_INDEX),
        permissions: [{ permissionType: ObjectActionListType.ECA_CASE_DATA, permissionActions: [ActionType.CREATE] }]
    }),
    new ContextMenuOption({
        name: 'Remove from Project Data',
        iconClass: 'fa-briefcase', iconColor: 'color-blue-grey-03', iconSecondClass: 'fa-minus', iconSecondColor: 'color-red-05',
        enabled: (item: GenericTreeNode) => (item?.data as Corpus).documentCount > 0 &&
            (item.data.representationType === RepresentationSet.VECTOR_SET || item.data.representationType === RepresentationSet.CONTENT_INDEX),
        permissions: [{ permissionType: ObjectActionListType.ECA_CASE_DATA, permissionActions: [ActionType.CREATE] }]
    }),
    new ContextMenuOption({ divider: true }),
    new ContextMenuOption({
        name: 'Find Exact Duplicates',
        iconClass: 'fa-files-o', iconColor: 'color-blue-grey-03', iconSecondClass: 'fa-search', iconSecondColor: 'color-yellow-07',
        enabled: (item: GenericTreeNode) => (item?.data as Corpus).documentCount > 0
    }),
    new ContextMenuOption({
        name: 'Find Content Duplicates',
        iconClass: 'fa-files-o', iconColor: 'color-blue-grey-03', iconSecondClass: 'fa-search', iconSecondColor: 'color-yellow-07',
        enabled: (item: GenericTreeNode) => (item?.data as Corpus).documentCount > 0
    }),
    new ContextMenuOption({ divider: true }),
    new ContextMenuOption({
        name: 'Calculate Word List',
        iconClass: 'fa-font', iconColor: 'color-blue-grey-03', iconSecondClass: 'fa-refresh', iconSecondColor: 'color-blue-01'
    }),
    new ContextMenuOption({
        name: 'View Word List ...', iconClass: 'fa-font', iconColor: 'color-blue-01', tooltip: 'Please Calculate Word List to view it'
    }),
    new ContextMenuOption({ divider: true }),
    new ContextMenuOption({
        name: 'Create Manifest ...', iconClass: 'fa-list-alt', iconColor: 'color-blue-01',
        enabled: (item: GenericTreeNode) => ((item?.data as Corpus).documentCount > 0),
        permissions: [{ permissionType: ObjectActionListType.DOCUMENT_REPORTS }]
    }),
    new ContextMenuOption({ divider: true }),
    new ContextMenuOption({
        name: 'Download All as PDFs', iconClass: 'fa-file-pdf-o', iconColor: 'color-blue-grey-03', iconSecondClass: 'fa-download', iconSecondColor: 'color-blue-01',
        enabled: (item: GenericTreeNode) => ((item?.data as Corpus).documentCount > 0),
        permissions: [{ permissionType: ObjectActionListType.DOWNLOAD_PDF }]
    }),
    new ContextMenuOption({
        name: 'Download All Natives', iconClass: 'fa-file', iconColor: 'color-blue-grey-03', iconSecondClass: 'fa-download', iconSecondColor: 'color-blue-01',
        enabled: (item: GenericTreeNode) => ((item?.data as Corpus).documentCount > 0),
        permissions: [{ permissionType: ObjectActionListType.DOWNLOAD_NATIVES }]
    }),
    new ContextMenuOption({ divider: true }),
    new ContextMenuOption({
        name: 'View Configuration ...', iconClass: 'fa-sliders', iconColor: 'color-grey-10',
        permissions: [{ permissionType: ObjectActionListType.CORPUS }]
    }),
    new ContextMenuOption({
        name: 'Update Patterns',
        enabled: (item: GenericTreeNode) => ((item?.data as Corpus).representationType === RepresentationSet.CONTENT_INDEX || (item?.data as Corpus).representationType === RepresentationSet.VECTOR_SET) &&
            ((item?.data as Corpus).sharedState !== SharedState.SHARED || ((item?.data as Corpus).sharedState === SharedState.SHARED && (item?.data as Corpus).handle.startsWith((item?.data as Corpus).owner))),
        tooltipFunction: (item: GenericTreeNode) => (item?.data as Corpus).sharedState === SharedState.SHARED && !((item?.data as Corpus).handle.startsWith((item?.data as Corpus).owner)) ? 'Action not allowed for a Data Set shared to this project.' : '',
        tooltipAlwaysOn: false,
        permissions: [{ permissionType: ObjectActionListType.CORPUS, permissionActions: [ActionType.CREATE] }]
    }),
    new ContextMenuOption({
        name: 'Reindex ...', iconClass: 'fa-refresh', iconColor: 'color-grey-10',
        enabled: (item: GenericTreeNode) => !(item?.data as Corpus).busy &&
            ((item?.data as Corpus).representationType === RepresentationSet.NO_INDEX || (item?.data as Corpus).representationType === RepresentationSet.CONTENT_INDEX) &&
            ((item?.data as Corpus).sharedState !== SharedState.SHARED || ((item?.data as Corpus).sharedState === SharedState.SHARED && (item?.data as Corpus).handle.startsWith((item?.data as Corpus).owner))),
        tooltipFunction: (item: GenericTreeNode) => (item?.data as Corpus).sharedState === SharedState.SHARED && !((item?.data as Corpus).handle.startsWith((item?.data as Corpus).owner)) ? 'Action not allowed for a Data Set shared to this project.' : '',
        tooltipAlwaysOn: false,
        permissions: [{ permissionType: ObjectActionListType.CORPUS, permissionActions: [ActionType.CREATE] }]
    }),
    new ContextMenuOption({
        nameFunction: (item: GenericTreeNode) => (item?.data as Corpus).sharedState === SharedState.SHARED ? 'Unshare' : 'Share',
        enabled: (item: GenericTreeNode) => (!(item?.data as Corpus).loadFileDataSet) && ((item?.data as Corpus).documentCount > 0) &&
            ((item?.data as Corpus).sharedState !== SharedState.SHARED || ((item?.data as Corpus).sharedState === SharedState.SHARED && (item?.data as Corpus).handle.startsWith((item?.data as Corpus).owner))),
        tooltipFunction: (item: GenericTreeNode) => (item?.data as Corpus).sharedState === SharedState.SHARED && !((item?.data as Corpus).handle.startsWith((item?.data as Corpus).owner)) ? 'Action not allowed for a Data Set shared to this project.' : '',
        tooltipAlwaysOn: false,
        permissions: [{ permissionType: ObjectActionListType.CORPUS, permissionActions: [ActionType.CREATE] }]
    }),
    new ContextMenuOption({
        name: 'Copy to Document Storage',
        iconClass: 'fa-database', iconColor: 'color-grey-05', iconSecondClass: 'fa-plus', iconSecondColor: 'color-green-04',
        enabled: (item: GenericTreeNode) => (!(item?.data as Corpus).sourceDataInDocStorage) && ((item?.data as Corpus).documentCount > 0) &&
            ((item?.data as Corpus).sharedState !== SharedState.SHARED || ((item?.data as Corpus).sharedState === SharedState.SHARED && (item?.data as Corpus).handle.startsWith((item?.data as Corpus).owner))),
        tooltipFunction: (item: GenericTreeNode) => (item?.data as Corpus).sharedState === SharedState.SHARED && !((item?.data as Corpus).handle.startsWith((item?.data as Corpus).owner)) ? 'Action not allowed for a Data Set shared to this project.' : '',
        tooltipAlwaysOn: false,
        permissions: [{ permissionType: ObjectActionListType.CORPUS, permissionActions: [ActionType.CREATE] }]
    }),
    new ContextMenuOption({ divider: true }),
    new ContextMenuOption({
        name: 'Edit ...', iconClass: 'fa-pencil', iconColor: 'color-blue-01',
        enabled: (item: GenericTreeNode) => ((item?.data as Corpus).sharedState !== SharedState.SHARED || ((item?.data as Corpus).sharedState === SharedState.SHARED && (item?.data as Corpus).handle.startsWith((item?.data as Corpus).owner))),
        tooltipFunction: (item: GenericTreeNode) => (item?.data as Corpus).sharedState === SharedState.SHARED && !((item?.data as Corpus).handle.startsWith((item?.data as Corpus).owner)) ? 'Action not allowed for a Data Set shared to this project.' : '',
        tooltipAlwaysOn: false,
        permissions: [{ permissionType: ObjectActionListType.CORPUS, permissionActions: [ActionType.CREATE] }]
    }),
    new ContextMenuOption({
        name: 'Delete', iconClass: 'fa-trash', iconColor: 'color-red-05',
        enabled: (item: GenericTreeNode) => !((item?.data as Corpus).sharedState === SharedState.SHARED && (item?.data as Corpus).handle.startsWith((item?.data as Corpus).owner)),
        tooltipFunction: (item: GenericTreeNode) => (item?.data as Corpus).sharedState === SharedState.SHARED || !((item?.data as Corpus).handle.startsWith((item?.data as Corpus).owner)) ? 'This Shared Data Set is currently shared with other project and cannot be deleted.' : '',
        tooltipAlwaysOn: false,
        permissions: [{ permissionType: ObjectActionListType.CORPUS, permissionActions: [ActionType.DELETE] }]
    })
];

export const ImportMenu = new GenericTreeNode({
    name: 'Imports',
    entityType: EntityType.import,
    automationId: 'nav-tree-imports',
    navigationInfo: new NavigationInfo({ name: 'Summary', path: 'summary', default: true }, { name: 'Communication Grid', path: 'communication-matrix' },
        { name: 'Domain Grid', path: 'domain-matrix' }, { name: 'Reports', path: 'reports' }),
    isExpandable: true,
    isExpandedOnSelect: true,
    isSortable: true,
    permissions: [{ permissionType: ObjectActionListType.CORPUS }],

    requestInfo: new DrRequestInfo({
        endpoint: Endpoint.corpusSetManager_listCorporaInCorpusSet,
        responseName: 'corpora'
    }),

    createChild: (data: any, handler?: any): GenericTreeNode => {
        const item = new Corpus(data);
        return new GenericTreeNode({
            name: item.name,
            entityType: EntityType.import,
            domId: `node_${item?.handle}`,
            navigationInfo: new NavigationInfo(
                { name: 'All Docs', path: 'document-list', default: true },
                { name: 'Exceptions', path: 'import-exceptions' },
                { name: 'Communication Grid', path: 'communication-matrix' },
                { name: 'Domain Grid', path: 'domain-matrix' },
                { name: 'Reports', path: 'reports' }),
            customNumber: item.documentCount,
            childPadding: 0,
            hasExpanderPadding: true,
            icon: item.smf ? 'fa-comments-o' : 'fa-cube',
            iconSecond: item.sharedState === SharedState.SHARED ? 'fa-circle-thin' : '',
            iconColor: 'color-purple-01',
            bundle: { isSharedDataSetNode: (item.sharedState === SharedState.SHARED ? true : false) },
            contextMenuOptions: importContextMenuOptions,
            onContextMenuAction: ImportMenu.onContextMenuAction,
            data: item,
            automationId: 'import-' + item.name
        }, handler);
    },

    showContextMenuButton: true,
    contextMenuOptions: [
        new ContextMenuOption({
            name: 'New Data Set ...', iconClass: 'fa-plus', iconColor: 'color-green-04', permissions: [
                { permissionType: ObjectActionListType.CORPUS, permissionActions: [ActionType.CREATE] },
            ]
        }),
        new ContextMenuOption({
            name: 'New Data Set from Load File ...', iconClass: 'fa-plus', iconColor: 'color-green-04', permissions: [
                { permissionType: ObjectActionListType.CORPUS, permissionActions: [ActionType.CREATE] },
            ]
        }),
        new ContextMenuOption({
            name: 'New Data Set for Short Message Format ...', iconClass: 'fa-plus', iconColor: 'color-green-04', permissions: [
                { permissionType: ObjectActionListType.CORPUS, permissionActions: [ActionType.CREATE] },
            ]
        }),
        new ContextMenuOption({
            name: 'Add Shared Data Set ...', permissions: [
                { permissionType: ObjectActionListType.CORPUS, permissionActions: [ActionType.CREATE] },
            ]
        }),
        new ContextMenuOption({ divider: true }),
        new ContextMenuOption({
            name: 'Calculate Word List',
            iconClass: 'fa-font', iconColor: 'color-blue-grey-03', iconSecondClass: 'fa-refresh', iconSecondColor: 'color-blue-01'
        }),
        new ContextMenuOption({ name: 'View Word List ...', iconClass: 'fa-font', iconColor: 'color-blue-01' })
    ],
});
